import { Form, Input, Select, Button, Row, Col, message, DatePicker } from 'antd';
import { formateDbDocuments, GenderEnumeratorFrontend, IDocument, IItemsWithCount, ILeadValues, IUserEntity, KeyValueRecord, ProvinceEnumerator, SegmentEnumerator } from '@rasayi-workspace/shared';
import HeadingTypography from 'src/app/components/shared/Typography/Heading';
import { LeadStatusEnum } from '@rasayi-workspace/shared';
import { useLeadContext } from '@contexts';
import { useEffect, useMemo } from 'react';
import AlertBar from 'src/app/components/shared/Alert';
import dayjs from 'dayjs';
import { State, City } from 'country-state-city';
import { debounce } from 'lodash';
import { GetTableItems } from '@services';
import { DEFAULT_TABLE_STATE } from '@constants';

const { Item } = Form;
const { Option } = Select;

const LeadInfoTab = ({ onClose, onSubmit, saveData, initialValue, isLoading, error, setError, setLeadDocuments }: any) => {
    const { channels, agents } = useMemo(() => useLeadContext(), []);
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();

    const pakStates = useMemo(
        () =>
            State.getStatesOfCountry('PK').map(state => {
                return {
                    code: state.isoCode,
                    value: state.name,
                    label: state.name,
                };
            }),
        [State]
    );

    const provinceCities = useMemo(() => {
        const selectedState = pakStates.find(state => state.label === initialValue.province);
        if (selectedState) {
            return City.getCitiesOfState('PK', selectedState.code).map(state => {
                return {
                    value: state.name,
                    label: state.name,
                };
            });
        }
        return [];
    }, [initialValue.province]);

    const leadStatusOptions = Object.values(LeadStatusEnum).map(status => ({
        label: status,
        value: status,
    }));
    const genderOptions = Object.values(GenderEnumeratorFrontend).map(gender => ({
        label: gender,
        value: gender,
    }));
    const provinceOptions = Object.values(ProvinceEnumerator).map(province => ({
        label: province,
        id: province,
    }));
    const segmentsOptions = Object.values(SegmentEnumerator).map(segment => ({
        label: segment,
        value: segment,
    }));

    const handleSubmit = (values: ILeadValues) => {
        const removeUndefinedKeys = Object.fromEntries(Object.entries(values).filter(([_, value]) => value !== undefined));
        onSubmit(removeUndefinedKeys);
    };

    useEffect(() => {
        if (initialValue && Object.keys(initialValue).length) {
            const transformed = {
                ...initialValue,
                dob: initialValue.dob ? dayjs(initialValue.dob) : null,
            };
            form.setFieldsValue(transformed);
        }
    }, [initialValue]);

    const fetchCnicUser = async (cnic: string) => {
        try {
            const query = `filter=roles.name,eq,customer;cnic_number,eq,${cnic}`;

            const returnedResult: IItemsWithCount<IUserEntity> = await GetTableItems<IUserEntity>(`user?${query}`, ['documents', 'roles'], [], DEFAULT_TABLE_STATE);
            if (returnedResult.items?.length) setUserValuesInForm(returnedResult.items[0]);
        } catch (error) {}
    };

    const setUserValuesInForm = (values: Partial<IUserEntity>) => {
        const customer = {
            first_name: values.first_name || '',
            last_name: values.last_name || '',
            email: values.email || '',
            phone: values.primary_mobile,
            cnic_number: values.cnic_number,
            dob: values.dob ? dayjs(values.dob) : null,
            gender: values.gender || null,
            province: values.province || '',
            city: values.city || '',
            address: values.address_line1 || '',
        };
        const transformed = {
            ...initialValue,
            ...customer,
        };

        form.setFieldsValue(transformed);

        saveData({ ...transformed, dob: transformed.dob ? (transformed.dob as unknown as dayjs.Dayjs).format('YYYY-MM-DD') : null });
        const documents: IDocument = formateDbDocuments(values?.documents || []);

        setLeadDocuments(documents);
    };

    const fetchCnicDetails = useMemo(() => {
        return debounce(cnic => {
            fetchCnicUser(cnic);
        }, 500);
    }, []);

    return (
        <>
            {contextHolder}
            <AlertBar error={error} setError={setError} />

            <Form
                layout="vertical"
                form={form}
                onFinish={handleSubmit}
                scrollToFirstError={false}
                onValuesChange={(_: KeyValueRecord, values) => {
                    const formatValues = { ...values };
                    if (values.dob) formatValues.dob = (values.dob as unknown as dayjs.Dayjs).format('YYYY-MM-DD');
                    saveData(formatValues);
                    if (_.hasOwnProperty('cnic_number')) {
                        const cnicRegex = /^\d{5}-\d{7}-\d$/;
                        if (values.cnic_number === '') setUserValuesInForm({});
                        else if (cnicRegex.test(values.cnic_number)) fetchCnicDetails(values.cnic_number);
                    }
                }}
            >
                <Row gutter={[12, 0]}>
                    {[
                        {
                            label: 'Agent',
                            name: 'user_id',
                            type: 'select',
                            required: true,
                            options: agents?.map(obj => {
                                return { label: obj.name, value: obj.id };
                            }),
                        },
                        { label: 'First Name', name: 'first_name', type: 'input', required: true },
                        { label: 'Last Name', name: 'last_name', type: 'input', required: true },
                        {
                            label: 'Email',
                            name: 'email',
                            type: 'input',
                            required: true,
                            rules: [{ type: 'email' as const, message: 'Enter a valid email address!' }],
                        },
                        { label: 'Phone Number', name: 'phone', type: 'input', required: true },
                        { label: 'Secondary Phone', name: 'secondary_phone', type: 'input' },

                        {
                            label: 'CNIC',
                            name: 'cnic_number',
                            type: 'input',
                            required: true,
                            rules: [
                                {
                                    pattern: /^\d{5}-\d{7}-\d$/,
                                    message: 'CNIC format must be XXXXX-XXXXXXX-X.',
                                },
                            ],
                        },
                        { label: 'DOB', name: 'dob', type: 'date', required: true, maxLength: 13 },
                        { label: 'Gender', name: 'gender', type: 'select', required: true, options: genderOptions },
                        { label: 'Status', name: 'lead_status', type: 'select', options: leadStatusOptions },
                        { label: 'Province', name: 'province', type: 'select', required: true, options: pakStates },
                        { label: 'City', name: 'city', type: 'select', required: true, options: provinceCities },
                        { label: 'Address', name: 'address', type: 'input', required: true },
                        { label: 'Secondary Address', name: 'secondary_address', type: 'input' },
                        { label: 'Organization', name: 'organization', type: 'input' },
                        { label: 'Segment', name: 'segment', type: 'select', required: true, options: segmentsOptions },
                        {
                            label: 'Channel',
                            name: 'channel_id',
                            type: 'select',
                            required: true,
                            options: channels.map(obj => {
                                return { label: obj.name, value: obj.id };
                            }),
                        },
                        { label: 'Comment', name: 'comments', type: 'input' },
                    ].map(({ label, name, type, required, options, rules, maxLength }) => (
                        <Col span={8} key={name} style={{ marginTop: '0px' }}>
                            <Item
                                label={
                                    <HeadingTypography fontSize={14} classNames="flex items-center h-[26px] font-normal">
                                        {label} {required && <span className="text-[#BD273B]"> *</span>}
                                    </HeadingTypography>
                                }
                                name={name}
                                rules={rules}
                                // validateTrigger="onSubmit"
                                validateTrigger={['onSubmit', 'onChange', 'onBlur']}
                            >
                                {type === 'select' ? (
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        className="lead-detail-selector w-full"
                                        allowClear
                                        placeholder={`Select ${label}`}
                                        filterSort={(optionA, optionB) => {
                                            return (optionA?.label ?? '')
                                                .toString()
                                                .toLowerCase()
                                                .localeCompare((optionB?.label ?? '').toString().toLowerCase());
                                        }}
                                        options={options}
                                    />
                                ) : type === 'date' ? (
                                    <DatePicker
                                        onChange={date => {
                                            form.setFieldValue('dob', date);
                                        }}
                                        style={{ width: '100%' }}
                                        changeOnBlur
                                    />
                                ) : type === 'input' ? (
                                    <Input maxLength={maxLength} style={{ height: '35px' }} placeholder={label} />
                                ) : null}
                            </Item>
                        </Col>
                    ))}
                </Row>

                <div>
                    <div className="flex justify-end items-center">
                        <Button onClick={onClose} className="mr-2 text-red-700 border-red-700 bg-white hover:bg-red-700 !hover:text-white !hover:border-white transition-colors">
                            Cancel
                        </Button>

                        <Button
                            loading={isLoading}
                            htmlType="submit"
                            className="bg-red-700 text-white border-red-700 hover:bg-red-800 hover:border-red-800 hover:text-white !text-white transition-colors"
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </Form>
        </>
    );
};

export default LeadInfoTab;
